import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs';
import { selectPageBySlug } from '../../store/webshop/webshop.selectors';
import { HeaderComponent } from '../../components/header/header.component';
import { ContainerComponent } from '../../components/container/container.component';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'app-page',
  standalone: true,
  imports: [HeaderComponent, ContainerComponent],
  template: `
    <app-container>
      <app-header id="header"></app-header>
      <div class="page-container">
        @if (page()) {
          <div class="page-content-box">
            <h1 class="page-title">{{ page()?.title }}</h1>
            <div class="page-content" [innerHTML]="page()?.content"></div>
          </div>
        }
      </div>
    </app-container>
  `,
  styles: [`
    app-header {
      top: 0;
      position: sticky;
      z-index: 9998;
    }

    .page-container {
      margin-top: 2rem;
      margin-bottom: 5rem;
      width: 100%;
      box-sizing: border-box;
    }

    .page-content-box {
      background: var(--product-background);
      outline: 0.4rem solid var(--border-gold);
      border: 0.7rem solid var(--product-inner-border);
      outline-offset: -0.4rem;
      padding: 4rem;
      box-sizing: border-box;
    }

    .page-title {
      font-family: var(--font-header);
      margin-bottom: 2rem;
      text-transform: uppercase;
      color: var(--text-dark);
      margin-top: 0;
      text-align: center;
      font-weight: 100;
      overflow-wrap: break-word;
      font-size: 1.5rem;
    }

    .page-content {
      line-height: 1.6;
      color: var(--product-text);
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;

      img {
        max-width: 100%;
        height: auto;
      }
    }

    @media only screen and (max-width: 650px) {
      .page-container {
        padding: 0 1rem;
      }

      .page-content-box {
        padding: 2rem 1rem;
      }
    }
  `]
})
export class PageComponent implements OnInit {
  page = signal<any | undefined>(undefined);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private titleService = inject(Title);
  private metaService = inject(Meta);

  ngOnInit() {
    this.route.params.pipe(
      map(params => params['slug']),
      switchMap(slug => this.store.select(selectPageBySlug(slug))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(page => {
      this.page.set(page);
      if (page) {
        this.titleService.setTitle(page.seoTitle || page.title);
        this.metaService.updateTag({ name: 'description', content: page.seoDescription || '' });
        this.metaService.updateTag({ name: 'keywords', content: page.seoKeywords || '' });

        // OpenGraph
        this.metaService.updateTag({ property: 'og:title', content: page.ogTitle || page.seoTitle || page.title });
        this.metaService.updateTag({ property: 'og:description', content: page.ogDescription || page.seoDescription || '' });
        if (page.ogImage) {
          this.metaService.updateTag({ property: 'og:image', content: page.ogImage });
        } else {
          this.metaService.removeTag("property='og:image'");
        }
      }
    });
  }
}
